import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { Status } from '@/features/bidding/constants'
import type { AllocatedVolume, AllocatedVolumes } from '@/features/bidding/types/allocatedVolumes'

interface BiddingDashboardContentProps {
  activationGroups: ActivationGroup[] | undefined
  allocatedVolumes: AllocatedVolumes
}

const useBiddingTableData = (activationGroups: ActivationGroup[] | undefined, allocatedVolumes: AllocatedVolumes) => {
  const { groupToRoMap, regulationObjectNames, roCodes } = useMemo(() => {
    const groupToRoMap = new Map<string, string>()
    const regulationObjectNames = new Map<string, string>()
    const uniqueRoCodes = new Set<string>()

    activationGroups?.forEach((group) => {
      const roCode = group.regulationObjectCode || group.code
      const roName = group.code.split(/[\s_]/)[0] // group names usually start like "NWX something something"

      groupToRoMap.set(group.uuid, roCode)
      uniqueRoCodes.add(roCode)
      regulationObjectNames.set(roCode, roName)
    })

    return {
      groupToRoMap,
      regulationObjectNames,
      roCodes: Array.from(uniqueRoCodes).sort((a, b) => a.localeCompare(b)),
    }
  }, [activationGroups])

  const allocationsByMarketAndRo = useMemo(() => {
    const allocations = new Map<string, Map<string, AllocatedVolume>>()
    allocatedVolumes.allocatedVolumes.forEach((allocatedVolume) => {
      const roCode = allocatedVolume.activationGroupUuid
        ? groupToRoMap.get(allocatedVolume.activationGroupUuid)
        : undefined
      if (!roCode) return
      const marketKey = allocatedVolume.marketProgram + (allocatedVolume.bidType ? ' ' + allocatedVolume.bidType : '')

      const thisMarketAllocationMap = allocations.get(marketKey) ?? new Map<string, AllocatedVolume>()
      thisMarketAllocationMap.set(roCode, allocatedVolume)
      allocations.set(marketKey, thisMarketAllocationMap)
    })
    return allocations
  }, [allocatedVolumes, groupToRoMap])

  const marketKeys = useMemo(() => Array.from(allocationsByMarketAndRo.keys()), [allocationsByMarketAndRo])

  return {
    roCodes,
    regulationObjectNames,
    allocationsByMarketAndRo,
    marketKeys,
  }
}

export const BiddingDashboardOverallBidTable = ({
  activationGroups,
  allocatedVolumes,
}: BiddingDashboardContentProps) => {
  const { t } = useTranslation()

  const { roCodes, regulationObjectNames, allocationsByMarketAndRo, marketKeys } = useBiddingTableData(
    activationGroups,
    allocatedVolumes,
  )

  const handleCopy = () => {
    if (roCodes.length === 0 || marketKeys.length === 0) return

    const headers = ['Regulation Object', ...marketKeys].join('\t')
    const rows = roCodes.map((roCode) => {
      const values = marketKeys.map((marketKey) => {
        const allocation = allocationsByMarketAndRo.get(marketKey)?.get(roCode)
        if (!allocation) return ''
        const isAllZeroOffered = allocation.ptus.length > 0 && allocation.ptus.every((ptu) => ptu.volume.quantity === 0)
        return isAllZeroOffered ? '0MW' : allocation.status.toString()
      })
      return [roCode, ...values].join('\t')
    })

    const tsv = [headers, ...rows].join('\n')
    navigator.clipboard.writeText(tsv)
  }

  const renderStatus = (allocatedVolume: AllocatedVolume) => {
    const isAllZero = allocatedVolume.ptus.length > 0 && allocatedVolume.ptus.every((ptu) => ptu.volume.quantity === 0)

    if (isAllZero) {
      return (
        <Tooltip title={'0MW'}>
          <FiberManualRecordIcon fontSize="small" sx={{ color: 'grey.400' }} />
        </Tooltip>
      )
    }

    switch (allocatedVolume.status) {
      case 'SCHEDULED':
      case Status.ACCEPTED:
      case Status.MANUALLY_ACCEPTED:
      case Status.MANUALLY_EDITED:
      case Status.OVERBID_ACCEPTED:
      case Status.BUYBACK:
        return (
          <Tooltip title={allocatedVolume.status}>
            <DoneAllIcon color="success" fontSize="small" />
          </Tooltip>
        )
      case Status.REJECTED:
        return (
          <Tooltip title={t('bidding.status.REJECTED')}>
            <CloseIcon color="error" fontSize="small" />
          </Tooltip>
        )
      case Status.PARTIALLY_ACCEPTED:
        return (
          <Tooltip title={t('bidding.status.PARTIALLY_ACCEPTED')}>
            <CheckIcon fontSize="small" sx={{ color: '#96B245' }} />
          </Tooltip>
        )
      case Status.OFFERED:
        return (
          <Tooltip title={t('bidding.status.OFFERED')}>
            <HourglassTopIcon fontSize="small" sx={{ color: '#1484a0' }} />
          </Tooltip>
        )
      default:
        // For other statuses, show a question mark
        return (
          <Tooltip title={allocatedVolume.status}>
            <Typography sx={{ fontWeight: 'bold' }} variant="body2">
              ?
            </Typography>
          </Tooltip>
        )
    }
  }

  if (!activationGroups || allocatedVolumes.allocatedVolumes.length === 0) {
    return (
      <Typography sx={{ p: 2 }} variant="body2">
        No allocated volumes found for the selected delivery day.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography>Overall bid and trade status</Typography>
        <Button size="small" startIcon={<ContentCopyIcon />} variant="outlined" onClick={handleCopy}>
          Copy to Clipboard
        </Button>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Table stickyHeader aria-label="bidding dashboard table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography fontWeight="bold">Regulation Object</Typography>
                </TableCell>
                {marketKeys.map((marketKey) => (
                  <TableCell key={marketKey} align="center">
                    <Typography noWrap fontWeight="bold">
                      {marketKey}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {roCodes.map((roCode) => (
                <TableRow key={roCode}>
                  <TableCell component="th" scope="row">
                    <Stack direction="row" spacing={2}>
                      <Typography fontFamily={'monospace'}>{roCode}</Typography>
                      <Typography> ({regulationObjectNames.get(roCode)})</Typography>
                    </Stack>
                  </TableCell>
                  {marketKeys.map((marketKey) => {
                    const allocation = allocationsByMarketAndRo.get(marketKey)?.get(roCode)
                    return (
                      <TableCell key={marketKey} align="center">
                        {allocation ? renderStatus(allocation) : ''}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}
